import { DroppedContentFile } from "../../../api/models/content";

const FOLDER_NAME_MIN_LENGTH = 1;
const FOLDER_NAME_MAX_LENGTH = 255;
export const FOLDER_MAXIMUM_NESTING_LEVEL = 4;
export const IMPORT_BATCH_MAX_ITEMS = 50;

export function normalizeSourceRelativePath(sourceRelativePath: string): string {
  return sourceRelativePath.replace(/\\/g, "/").split("/").filter(Boolean).join("/");
}

function isFolderPartValid(folderPart: string): boolean {
  const hasInvalidWindowsCharacters = /[<>:"|?*]/.test(folderPart);

  return (
    folderPart.length >= FOLDER_NAME_MIN_LENGTH &&
    folderPart.length <= FOLDER_NAME_MAX_LENGTH &&
    folderPart.trim() === folderPart &&
    folderPart !== "." &&
    folderPart !== ".." &&
    !hasInvalidWindowsCharacters
  );
}

function readEntry(entry: FileSystemEntry, parentPath = ""): Promise<DroppedContentFile[]> {
  return new Promise((resolve, reject) => {
    const entryPath = normalizeSourceRelativePath(`${parentPath}/${entry.name}`);

    if (entry.isFile) {
      (entry as FileSystemFileEntry).file(
        (file) =>
          resolve([
            {
              file,
              sourceRelativePath: entryPath || file.name,
              hasReliableSourceRelativePath: true,
            },
          ]),
        (err) => reject(err)
      );
    } else if (entry.isDirectory) {
      const dirReader = (entry as FileSystemDirectoryEntry).createReader();
      const allFiles: DroppedContentFile[] = [];

      const readBatch = () => {
        dirReader.readEntries(
          async (entries) => {
            if (entries.length === 0) {
              resolve(allFiles);
            } else {
              for (const subEntry of entries) {
                const result = await readEntry(subEntry, entryPath);
                allFiles.push(...result);
              }
              readBatch();
            }
          },
          (err) => reject(err)
        );
      };

      readBatch();
    } else {
      resolve([]);
    }
  });
}

function getFileMetadataKey(file: File): string {
  return [file.name, file.size, file.lastModified, file.type].join(":");
}

function isSameFileReference(firstFile: File, secondFile: File): boolean {
  return firstFile === secondFile;
}

function hasRelativePath(sourceRelativePath: string, fileName: string): boolean {
  return normalizeSourceRelativePath(sourceRelativePath) !== normalizeSourceRelativePath(fileName);
}

export type DroppedFilesFromDataTransferResult = {
  droppedFiles: DroppedContentFile[];
  usedFallbackWithoutReliableEntries: boolean;
};

export function getSelectedContentFilesFromInputFiles(
  files: File[],
  options: { isFolderInput?: boolean } = {}
): DroppedContentFile[] {
  return files.map((selectedFile) => {
    const browserRelativePath = options.isFolderInput
      ? (selectedFile as File & { webkitRelativePath?: string }).webkitRelativePath
      : undefined;
    const hasReliableSourceRelativePath = Boolean(browserRelativePath);

    return {
      file: selectedFile,
      sourceRelativePath:
        browserRelativePath && hasReliableSourceRelativePath
          ? normalizeSourceRelativePath(browserRelativePath)
          : "",
      hasReliableSourceRelativePath,
    };
  });
}

export async function getDroppedFilesFromDataTransfer(
  dataTransfer: DataTransfer
): Promise<DroppedFilesFromDataTransferResult> {
  const dataTransferItemSnapshots = Array.from(dataTransfer.items ?? []).map((item, itemIndex) => {
    const entry = item.webkitGetAsEntry?.();
    const fileFromItem = item.kind === "file" ? item.getAsFile() : null;

    return {
      itemIndex,
      kind: item.kind,
      entry,
      fileFromItem,
    };
  });
  const dataTransferFileSnapshots = Array.from(dataTransfer.files ?? []);
  let usedFallbackWithoutReliableEntries = false;

  const filesFromEntries: DroppedContentFile[] = [];
  const filesFromItemsWithoutEntries: DroppedContentFile[] = [];
  const entryCoveredTopLevelItemIndexes = new Set<number>();
  const topLevelFileItemIndexes = new Set(
    dataTransferItemSnapshots
      .filter(({ kind }) => kind === "file")
      .map(({ itemIndex }) => itemIndex)
  );
  const topLevelDirectoryEntryNames = new Set(
    dataTransferItemSnapshots
      .filter(({ entry }) => entry?.isDirectory)
      .map(({ entry }) => entry?.name)
      .filter((name): name is string => Boolean(name))
  );

  for (const dataTransferItemSnapshot of dataTransferItemSnapshots) {
    const { itemIndex, kind, entry, fileFromItem } = dataTransferItemSnapshot;

    if (entry) {
      const filesFromEntry = await readEntry(entry);

      if (filesFromEntry.length > 0) {
        filesFromEntries.push(...filesFromEntry);
        entryCoveredTopLevelItemIndexes.add(itemIndex);
        continue;
      }
    }

    if (kind === "file" && fileFromItem) {
      filesFromItemsWithoutEntries.push({
        file: fileFromItem,
        sourceRelativePath: fileFromItem.name,
        hasReliableSourceRelativePath: false,
      });
      usedFallbackWithoutReliableEntries = true;
    }
  }

  const normalizedFilesFromEntries = filesFromEntries.map((droppedFile) => ({
    ...droppedFile,
    sourceRelativePath: normalizeSourceRelativePath(droppedFile.sourceRelativePath),
  }));
  const normalizedFilesFromItemsWithoutEntries = filesFromItemsWithoutEntries.map(
    (droppedFile) => ({
      ...droppedFile,
      sourceRelativePath: normalizeSourceRelativePath(droppedFile.sourceRelativePath),
    })
  );

  const droppedFiles: DroppedContentFile[] = [...normalizedFilesFromEntries];
  const entryFileReferences = new Set(normalizedFilesFromEntries.map(({ file }) => file));
  const entryFilePlainMetadataKeys = new Set(
    normalizedFilesFromEntries
      .filter(({ file, sourceRelativePath }) => !hasRelativePath(sourceRelativePath, file.name))
      .map(({ file }) => getFileMetadataKey(file))
  );
  const entryCoveredTopLevelItemCount = entryCoveredTopLevelItemIndexes.size;
  const totalTopLevelFileItemCount = topLevelFileItemIndexes.size;
  const entriesCoverAllTopLevelFileItems =
    totalTopLevelFileItemCount > 0 && entryCoveredTopLevelItemCount === totalTopLevelFileItemCount;

  const addFallbackFile = (droppedFile: DroppedContentFile) => {
    const sourceRelativePath = normalizeSourceRelativePath(droppedFile.sourceRelativePath);
    const isPlainSourceRelativePath = !hasRelativePath(sourceRelativePath, droppedFile.file.name);
    const isDuplicateOfEntryByReference = entryFileReferences.has(droppedFile.file);
    const isDuplicateTopLevelFile =
      isPlainSourceRelativePath &&
      entryFilePlainMetadataKeys.has(getFileMetadataKey(droppedFile.file));

    if (isDuplicateOfEntryByReference || isDuplicateTopLevelFile) {
      return;
    }

    if (
      droppedFiles.some(
        (existingDroppedFile) =>
          isSameFileReference(existingDroppedFile.file, droppedFile.file) &&
          normalizeSourceRelativePath(existingDroppedFile.sourceRelativePath) === sourceRelativePath
      )
    ) {
      return;
    }

    droppedFiles.push({ ...droppedFile, sourceRelativePath });
  };

  normalizedFilesFromItemsWithoutEntries.forEach((droppedFile) => addFallbackFile(droppedFile));

  for (const file of dataTransferFileSnapshots) {
    if (entriesCoverAllTopLevelFileItems) {
      continue;
    }

    if (topLevelDirectoryEntryNames.has(file.name)) {
      continue;
    }

    usedFallbackWithoutReliableEntries = true;
    addFallbackFile({
      file,
      sourceRelativePath: file.name,
      hasReliableSourceRelativePath: false,
    });
  }

  return { droppedFiles, usedFallbackWithoutReliableEntries };
}

export async function getFilesFromDataTransferItems(items: DataTransferItemList): Promise<File[]> {
  const files: File[] = [];

  for (const item of Array.from(items)) {
    const file = item.getAsFile();
    if (file) {
      files.push(file);
    }
  }

  return files;
}

export function getSourceRelativePathFolderParts(sourceRelativePath: string): string[] {
  return normalizeSourceRelativePath(sourceRelativePath).split("/").filter(Boolean).slice(0, -1);
}

export function getRenderableSourceRelativePath(sourceRelativePath?: string | null): string | null {
  if (typeof sourceRelativePath !== "string" || sourceRelativePath.trim().length === 0) {
    return null;
  }

  const normalizedSourceRelativePath = normalizeSourceRelativePath(sourceRelativePath);
  const rawSegments = sourceRelativePath.replace(/\\/g, "/").split("/");
  const hasEmptyBoundarySegment = rawSegments[0] === "" || rawSegments.at(-1) === "";
  const effectiveSegments = rawSegments.filter(Boolean);

  if (hasEmptyBoundarySegment || effectiveSegments.length < 2) {
    return null;
  }

  return normalizedSourceRelativePath;
}

export function hasRenderableSourceRelativePathFolder(sourceRelativePath?: string | null): boolean {
  return getRenderableSourceRelativePath(sourceRelativePath) !== null;
}

export function getRenderableSourceRelativeFolderPath(
  sourceRelativePath?: string | null
): string | null {
  const renderableSourceRelativePath = getRenderableSourceRelativePath(sourceRelativePath);

  if (renderableSourceRelativePath === null) {
    return null;
  }

  const folderParts = getSourceRelativePathFolderParts(renderableSourceRelativePath);

  if (folderParts.length === 0) {
    return null;
  }

  return folderParts.join("/");
}

export function hasSourceRelativePathFolder(sourceRelativePath?: string | null): boolean {
  return getRenderableSourceRelativeFolderPath(sourceRelativePath) !== null;
}

export function shouldPreserveFolderStructure(
  contentItem: { sourceRelativePath?: string | null; hasReliableSourceRelativePath?: boolean },
  options: { isMultipleUploadContext: boolean }
): boolean {
  return (
    options.isMultipleUploadContext &&
    contentItem.hasReliableSourceRelativePath === true &&
    hasSourceRelativePathFolder(contentItem.sourceRelativePath)
  );
}

export function isSourceRelativePathValid(sourceRelativePath: string): boolean {
  const normalizedSourceRelativePath = normalizeSourceRelativePath(sourceRelativePath);
  const renderableSourceRelativePath = getRenderableSourceRelativePath(sourceRelativePath);
  const effectiveSegments = sourceRelativePath.replace(/\\/g, "/").split("/").filter(Boolean);

  if (
    renderableSourceRelativePath === null ||
    normalizedSourceRelativePath.length < FOLDER_NAME_MIN_LENGTH ||
    normalizedSourceRelativePath.length > FOLDER_NAME_MAX_LENGTH
  ) {
    return false;
  }

  if (
    !effectiveSegments.every(
      (segment) => segment.trim() === segment && segment !== "." && segment !== ".."
    )
  ) {
    return false;
  }

  return getSourceRelativePathFolderParts(normalizedSourceRelativePath).every(isFolderPartValid);
}

export function validateDroppedContentPaths(
  contents: { sourceRelativePath?: string; hasReliableSourceRelativePath?: boolean }[]
): boolean {
  return contents.every((content) => {
    if (!shouldPreserveFolderStructure(content, { isMultipleUploadContext: true })) {
      return true;
    }

    return isSourceRelativePathValid(content.sourceRelativePath ?? "");
  });
}

export async function generateFileFromBlob(url: string): Promise<File> {
  const response = await fetch(url);
  const blob = await response.blob();

  return new File([blob], `thumbnail.jpeg`, { type: "image/jpeg", lastModified: Date.now() });
}
